import { useState } from 'react';
import { toast } from 'sonner';
import { useSpaceWrite } from '@/hooks/queries/useSpaceWrite';
import { spacesApi } from '@/lib/api';
import { settingsError } from '@/lib/spaces/settings';
import type { SpaceDetail, SpaceRole } from '@/types/spaces';

// Percentages and the roster change every unsettled split and balance of this space.
const SPACE_READS = ['space-expenses-page', 'space-expenses', 'space-ledger', 'space-home', 'space-audit', 'space-commitments', 'space-budget'];
// Personal reads that list spaces and rules; a roster change or a closed trip pauses or drops entries in them.
const PERSONAL_READS = ['spaces', 'space-rules-overview', 'space-send-rules'];

export function useSpaceSettings(spaceId: string) {
  const { write, remember, mutate, refresh, userId } = useSpaceWrite();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async <T,>(action: string, fallback: string, operation: (token: string) => Promise<T>): Promise<T | undefined> => {
    if (busy) return undefined;
    setBusy(action); setError(null);
    try {
      return await write(operation);
    } catch (failure) {
      const message = settingsError(failure, fallback);
      setError(message); toast.error(message);
      // A rejected change usually means the roster moved under this page.
      refresh(['space', userId, spaceId]);
      return undefined;
    } finally { setBusy(null); }
  };
  const revalidate = () => { void mutate((key) => Array.isArray(key) && key[1] === userId
    && (PERSONAL_READS.includes(key[0]) || (key[2] === spaceId && SPACE_READS.includes(key[0])))); };
  const applied = async (space: SpaceDetail | undefined, message: string): Promise<boolean> => {
    if (!space) return false;
    await remember(space);
    revalidate();
    toast.success(message);
    return true;
  };

  const savePercentages = async (members: { userId: string; pct: number }[]) =>
    applied(await run('percentages', 'Não foi possível salvar os percentuais. Tente novamente.', (token) => spacesApi.setPercentages(token, spaceId, { members })), 'Percentuais atualizados.');
  const equalize = async () =>
    applied(await run('percentages', 'Não foi possível igualar os percentuais. Tente novamente.', (token) => spacesApi.setPercentages(token, spaceId, { equalize: true })), 'Percentuais iguais entre todos.');
  const changeRole = async (memberId: string, role: SpaceRole) =>
    applied(await run(`role:${memberId}`, 'Não foi possível mudar a permissão. Tente novamente.', (token) => spacesApi.updateMember(token, spaceId, memberId, { role })), 'Permissão atualizada.');
  const closeTrip = async () =>
    applied(await run('close', 'Não foi possível encerrar a viagem. Tente novamente.', (token) => spacesApi.close(token, spaceId)), 'Viagem encerrada. O histórico continua disponível.');
  // Removing someone else keeps this space open; leaving (own id) takes the caller out of it.
  const removeMember = async (memberId: string): Promise<boolean> => {
    const leaving = memberId === userId;
    const fallback = leaving ? 'Não foi possível sair do espaço. Tente novamente.' : 'Não foi possível remover esta pessoa. Tente novamente.';
    const removed = await run(`remove:${memberId}`, fallback, async (token) => { await spacesApi.removeMember(token, spaceId, memberId); return true; });
    if (!removed) return false;
    // Someone who left must not see this space again from cache, neither its own reads (browser Back inside the
    // dedupe window) nor the personal lists that still name it (the caller lands on `/` right after this returns).
    // Those entries are dropped before success is reported; `revalidate` then refetches the personal ones.
    if (leaving) await mutate((key) => Array.isArray(key) && key[1] === userId && (key[2] === spaceId || PERSONAL_READS.includes(key[0])), undefined, { revalidate: false });
    else refresh(['space', userId, spaceId]);
    revalidate();
    return true;
  };

  return { busy, error, savePercentages, equalize, changeRole, closeTrip, removeMember };
}
